import {
  detectDragCollision,
  isOutsideShift,
  type ConflictItem,
  type MinuteRange,
} from '../../../shared/lib/schedule'
import type { PauseDayContext } from './types'

/**
 * Everything standing in the way of a proposed Pause or Abwesenheit.
 *
 * The three are reported separately because they read differently to the user, not because they
 * are treated differently: on this widget any one of them refuses the move.
 */
export interface PauseAdvisory {
  /** Blocks the range overlaps — a Termin, an Aufgabe, another Abwesenheit, or the Pause. */
  conflicts: ConflictItem[]
  /** Nothing is in the way; the technician simply is not working at that hour. */
  outsideShift: boolean
  /** The same refusal from the other direction — not a working day at all. */
  nonWorkingDay: boolean
}

/**
 * What is in the way of a proposed Pause or Abwesenheit.
 *
 * Replaces `checkPauseCollisions`, which walked appointments, absences and the break by hand and
 * missed scheduled tasks entirely — so a Pause dropped onto an Aufgabe raised nothing here and a
 * 409 there. Everything now collides against one `CalendarBlock[]`, so anything the calendar draws
 * is by construction something the calendar checks.
 */
export function findConflicts(
  proposed: MinuteRange,
  day: PauseDayContext,
  excludeKey: string,
): PauseAdvisory {
  if (!day.working) {
    return { conflicts: [], outsideShift: false, nonWorkingDay: true }
  }

  // `detectDragCollision` already drops blocks flagged `needsRescheduling` — they reserve nothing,
  // which is what the API's `overlapWhere` does too. The old check applied that rule to
  // appointments but not absences, so a flagged absence raised a conflict against a block that
  // `DayColumn` had already declined to draw.
  const conflicts = detectDragCollision(day.blocks, proposed, excludeKey).map((block) => ({
    label: block.label,
    timeLabel: block.timeLabel,
  }))

  return {
    conflicts,
    outsideShift: isOutsideShift(proposed, day.shift),
    nonWorkingDay: false,
  }
}

/**
 * Whether this widget refuses the move. **The one definition** — the drop handler uses it to decide
 * whether to send at all, and the popover uses it to gate Speichern, so the two cannot drift into
 * disagreeing about what is allowed.
 *
 * A Handwerker gets no override here. The board's `force` / `allowOutsideWorkingHours` confirmations
 * exist for a dispatcher deciding to double-book somebody; from a technician's own calendar the
 * answer to "this time is taken" is to pick another time. Double-booking and out-of-hours work
 * remain possible from `disposition-board`, which is where that call belongs.
 *
 * This is only sound because the check underneath is now accurate. It used to miss scheduled
 * Aufgaben and raise phantom conflicts against flagged absences the calendar had already declined
 * to draw — blocking on *that* would have refused perfectly good moves for invisible reasons.
 */
export function isBlocked(advisory: PauseAdvisory): boolean {
  return advisory.conflicts.length > 0 || advisory.outsideShift || advisory.nonWorkingDay
}
